import type { DateInput } from '@repo/common';
import {
  addDuration,
  DATE_FORMATS,
  formatWithPattern,
  getUserTimezone,
  isBefore,
  isSameDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subtractDuration,
} from '@repo/common';
export const getStartOfPeriod = (date: DateInput, periodType: 'week' | 'month' | 'year'): Date => {
  switch (periodType) {
    case 'week':
      return startOfWeek(date);
    case 'month':
      return startOfMonth(date);
    case 'year':
      return startOfYear(date);
    default:
      throw new Error('Invalid period type');
  }
};

// Function to get the 7 days of the week starting from `startOfWeek`
export function getWeekDays(startOfWeek: DateInput): Date[] {
  return Array.from({ length: 7 }).map((_, index) => addDuration(startOfWeek, index, 'day'));
}

function getMonthDays(startOfMonth: DateInput): Date[][] {
  const weeks = [];
  let current = startOfWeek(startOfMonth); // Start from the beginning of the week that contains the 1st day of the month

  const endOfCalendar = addDuration(addDuration(startOfMonth, 1, 'month'), -1, 'day'); // End on the last day of the month
  const endOfWeek = addDuration(startOfWeek(endOfCalendar), 6, 'day'); // End on the last day of the last week

  while (isBefore(current, endOfWeek) || isSameDay(current, endOfWeek)) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(current);
      current = addDuration(current, 1, 'day');
    }
    weeks.push(week);
  }

  return weeks;
}

export const getPreviousPeriod = (date: DateInput, periodType: 'week' | 'month' | 'year'): Date => {
  switch (periodType) {
    case 'week':
      return subtractDuration(date, 1, 'week');
    case 'month':
      return subtractDuration(date, 1, 'month');
    case 'year':
      return subtractDuration(date, 1, 'year');
    default:
      throw new Error('Invalid period type');
  }
};

// Reusable function to navigate between periods
export const getNextPeriod = (date: DateInput, periodType: 'week' | 'month' | 'year'): Date => {
  switch (periodType) {
    case 'week':
      return addDuration(date, 1, 'week');
    case 'month':
      return addDuration(date, 1, 'month');
    case 'year':
      return addDuration(date, 1, 'year');
    default:
      throw new Error('Invalid period type');
  }
};

// Function to get a single-letter label for the weekday (e.g., "S" for Sunday)
export function getWeekdayLetterLabel(date: DateInput): string {
  return formatWithPattern(date, DATE_FORMATS.DAY_ABBREV, getUserTimezone()).charAt(0);
}

// Function to get the first three letters of the weekday
export function getWeekdayLettersLabel(date: DateInput): string {
  return formatWithPattern(date, DATE_FORMATS.DAY_ABBREV, getUserTimezone());
}

// Helper function for fetching the days in the current period
export function getPeriodDays(date: DateInput, periodType: 'week' | 'month' | 'year'): Date[][] {
  switch (periodType) {
    case 'year':
      return Array.from({ length: 12 }, (_, i) => [addDuration(date, i, 'month')]);
    case 'week':
      return [getWeekDays(date)];
    case 'month':
      return getMonthDays(date);
    default:
      throw new Error('Invalid period type');
  }
}
